import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { FileDown, Presentation, Calendar, Loader2, Check } from 'lucide-react';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

const FUNCTIONS = [
  { key: 'dispatches',       label: 'Dispatches',      hint: 'Orders moved into a dispatched state'          },
  { key: 'receivings',       label: 'Receivings',      hint: 'ASN receipts marked as received'                },
  { key: 'putaway',          label: 'Put-Away',        hint: 'Completed put-away tasks on the floor'          },
  { key: 'stock_transfers',  label: 'Stock Transfers', hint: 'Movements between locations / warehouses'       },
  { key: 'returns',          label: 'Returns',         hint: 'Pallet / order returns received back to stock'  },
  { key: 'access_control',   label: 'Access Control (Time Management)', hint: 'Clock-in, worked hours, late minutes and overtime from the attendance log' },
  { key: 'overtime_requests',label: 'Overtime Requests', hint: 'Submitted, approved and rejected overtime hours with top requesters' },
  { key: 'desk_leaving',     label: 'Desk Leaving',    hint: 'Desk-away events, time away and success/unsuccess outcomes' },
  { key: 'pallets_ledger',   label: 'Pallets Ledger Performance', hint: 'Ledger movements (Qty In/Out), adjustment slips and reconciliations' },
];

const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

const METRIC_LABELS = {
  worked_hours: 'Worked (h)',
  late_hours: 'Late (h)',
  overtime_hours: 'Overtime (h)',
  approved_hours: 'Approved (h)',
  away_hours: 'Away (h)',
  ledger_in: 'Qty In',
  ledger_out: 'Qty Out',
  days_present: 'Days Present',
  asns: 'ASNs',
};
const labelFor = (k) => METRIC_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return String(v ?? '');
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
};

const AdminPerformanceReport = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [startDate, setStartDate] = useState(monthsAgo(5));
  const [endDate, setEndDate] = useState(today());
  const [enabled, setEnabled] = useState(() => new Set(FUNCTIONS.map((f) => f.key)));
  const [loading, setLoading] = useState(null); // 'pdf' | 'pptx' | null
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const picked = useMemo(() => FUNCTIONS.filter((f) => enabled.has(f.key)).map((f) => f.key), [enabled]);

  const toggle = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const validRange = startDate && endDate && startDate <= endDate;

  const body = () => ({ start_date: startDate, end_date: endDate, functions: picked });

  const refreshPreview = async () => {
    if (!validRange || picked.length === 0) return;
    setPreviewLoading(true);
    try {
      const r = await axios.post(
        `${API}/admin/reports/performance-presentation/data`,
        body(),
        { headers: authHeaders },
      );
      setPreview(r.data);
    } catch (err) {
      toast({
        title: 'Could not load preview',
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const download = async (kind) => {
    if (!validRange) {
      toast({ title: 'Pick a valid date range first.', variant: 'destructive' });
      return;
    }
    if (picked.length === 0) {
      toast({ title: 'Select at least one WMS function.', variant: 'destructive' });
      return;
    }
    setLoading(kind);
    try {
      const r = await axios.post(
        `${API}/admin/reports/performance-presentation/${kind}`,
        body(),
        { headers: authHeaders, responseType: 'blob' },
      );
      const blob = new Blob([r.data], {
        type: kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance-${startDate}_to_${endDate}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `${kind.toUpperCase()} downloaded.` });
    } catch (err) {
      toast({
        title: `Could not generate ${kind.toUpperCase()}`,
        description: err?.response?.data?.detail || err?.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="admin-performance-report-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-black text-[#0F172A]">Performance Presentation Report</h1>
        <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
          Pick a date range and the WMS functions you want on the deck.
          Both a presentation-ready PowerPoint and a matching PDF are
          generated with the same KPIs and monthly trend charts.
        </p>
      </div>

      {/* Range + function toggles */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-4 md:p-5 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block" data-testid="perf-report-start-date">
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Start date
            </div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-neutral-300 text-sm focus:border-sky-500 focus:outline-none"
              data-testid="perf-report-start-date-input"
            />
          </label>
          <label className="block" data-testid="perf-report-end-date">
            <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> End date
            </div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-neutral-300 text-sm focus:border-sky-500 focus:outline-none"
              data-testid="perf-report-end-date-input"
            />
          </label>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">Include sections</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {FUNCTIONS.map((f) => {
              const on = enabled.has(f.key);
              return (
                <label
                  key={f.key}
                  className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition ${
                    on
                      ? 'bg-sky-50/60 border-sky-200'
                      : 'bg-white border-neutral-200 hover:bg-neutral-50'
                  }`}
                  data-testid={`perf-report-fn-${f.key}`}
                >
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-md inline-flex items-center justify-center border ${
                      on ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-neutral-300'
                    }`}
                  >
                    {on && <Check className="w-3.5 h-3.5" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#0F172A]">{f.label}</div>
                    <div className="text-[11px] text-neutral-500 leading-snug">{f.hint}</div>
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={() => toggle(f.key)}
                  />
                </label>
              );
            })}
          </div>
          {picked.length === 0 && (
            <div className="mt-2 text-[11px] text-red-600 font-semibold">
              Select at least one function to generate the report.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button
            type="button"
            onClick={() => download('pdf')}
            disabled={loading !== null || picked.length === 0 || !validRange}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#0078D4] hover:bg-[#005A9E] rounded-full px-4 py-2 disabled:opacity-40"
            data-testid="perf-report-download-pdf-btn"
          >
            {loading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Download PDF
          </button>
          <button
            type="button"
            onClick={() => download('pptx')}
            disabled={loading !== null || picked.length === 0 || !validRange}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-white bg-[#059669] hover:bg-[#047857] rounded-full px-4 py-2 disabled:opacity-40"
            data-testid="perf-report-download-pptx-btn"
          >
            {loading === 'pptx' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" />}
            Download PowerPoint
          </button>
          <button
            type="button"
            onClick={refreshPreview}
            disabled={previewLoading || picked.length === 0 || !validRange}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-700 bg-white border border-neutral-300 hover:bg-neutral-50 rounded-full px-4 py-2 disabled:opacity-40"
            data-testid="perf-report-preview-btn"
          >
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Preview KPIs
          </button>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="rounded-2xl bg-white border border-neutral-200 p-4 md:p-5 space-y-5" data-testid="perf-report-preview">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Executive summary</div>
            <div className="text-sm text-neutral-600 leading-relaxed">{preview.summary.narrative}</div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {preview.summary.kpis.map((k) => (
              <div key={k.label} className="rounded-xl bg-neutral-50 border border-neutral-200 p-3">
                <div className="text-2xl font-black text-[#0078D4] tabular-nums">
                  {fmt(k.value)}
                </div>
                <div className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                  {k.unit}
                </div>
                <div className="text-xs font-bold text-[#0F172A] mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
          {preview.sections.map((s) => (
            <div key={s.key} className="rounded-xl border border-neutral-200 p-3" data-testid={`perf-report-section-${s.key}`}>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <div className="text-sm font-bold text-[#0F172A]">{s.label}</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
                  {preview.months.length} month{preview.months.length === 1 ? '' : 's'}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                {s.kpis.map((k) => (
                  <div key={k.label} className="text-xs">
                    <div className="font-bold tabular-nums text-[#0F172A]">
                      {fmt(k.value)}
                      <span className="text-[10px] text-neutral-500 font-normal ml-1">{k.unit}</span>
                    </div>
                    <div className="text-[10px] text-neutral-500">{k.label}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto rounded-md border border-neutral-100">
                <table className="w-full text-[11px]">
                  <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Month</th>
                      {s.series[0]
                        ? Object.keys(s.series[0]).filter((k) => k !== 'label').map((m) => (
                            <th key={m} className="px-2 py-1.5 text-right">{labelFor(m)}</th>
                          ))
                        : null}
                    </tr>
                  </thead>
                  <tbody>
                    {s.series.map((row) => (
                      <tr key={row.label} className="border-t border-neutral-100">
                        <td className="px-2 py-1.5 font-mono">{row.label}</td>
                        {Object.keys(row).filter((k) => k !== 'label').map((m) => (
                          <td key={m} className="px-2 py-1.5 text-right tabular-nums">
                            {fmt(row[m])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {s.top_table && Array.isArray(s.top_table.rows) && s.top_table.rows.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                    {s.top_table.title || 'Top entries'}
                  </div>
                  <div className="overflow-x-auto rounded-md border border-neutral-100">
                    <table className="w-full text-[11px]">
                      <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                        <tr>
                          {(s.top_table.columns || []).map((c) => (
                            <th key={c} className="px-2 py-1.5 text-left first:text-left [&:not(:first-child)]:text-right">{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {s.top_table.rows.map((r, i) => (
                          <tr key={i} className="border-t border-neutral-100">
                            {(s.top_table.columns || []).map((c, j) => (
                              <td key={c} className={`px-2 py-1.5 ${j === 0 ? 'text-left' : 'text-right tabular-nums'}`}>
                                {typeof r[c] === 'string' ? r[c] : fmt(r[c])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPerformanceReport;
